import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { MeResponse, TokenPairResponse } from "../api-models";
import { useMeQuery } from "../helpers/queries";
import { clearTokens, getAccessToken, setTokens } from "../helpers/auth";
import { api } from "../helpers/api";

interface UserContextValue {
  user: MeResponse | null;
  loading: boolean;
  // Why the last /me failed, so callers can tell a rejected session from an unreachable API.
  error: unknown;
  login: (pair: TokenPairResponse) => void;
  logout: () => Promise<void>;
  refetch: () => Promise<unknown>;
}

const UserContext = createContext<UserContextValue | null>(null);

export function UserProvider({ children }: { children: ReactNode }) {
  const [hasToken, setHasToken] = useState(!!getAccessToken());
  const queryClient = useQueryClient();
  const { data, isLoading, error, refetch } = useMeQuery(hasToken);

  const login = useCallback(
    (pair: TokenPairResponse) => {
      setTokens(pair.accessToken, pair.refreshToken);
      queryClient.setQueryData(["me"], pair.user);
      setHasToken(true);
    },
    [queryClient],
  );

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout", {});
    } catch {
      // best-effort; clear local state regardless
    }
    clearTokens();
    queryClient.removeQueries({ queryKey: ["me"] });
    setHasToken(false);
  }, [queryClient]);

  const value = useMemo<UserContextValue>(
    () => ({ user: data ?? null, loading: hasToken && isLoading, error, login, logout, refetch }),
    [data, hasToken, isLoading, error, login, logout, refetch],
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export function useUser() {
  const ctx = useContext(UserContext);
  if (!ctx) throw new Error("useUser must be used within UserProvider");
  return ctx;
}
